/*
 * The three states every screen shares, in one place.
 *
 * Loading, error and permission are required of all thirty-three destinations, and a screen that
 * writes its own is a screen that will write one of them differently — or forget the third, which
 * is what happens in practice: permission is the state nobody remembers because it never shows up
 * while you are building with your own owner token.
 *
 * `permitted` defaults to `true`, so a caller that has not decided yet renders its content rather
 * than a lock screen — the registry's `canAccess` is what answers this, not a guess here.
 */
import type { ReactNode } from 'react';
import { FeedbackState, LoadingRows, PermissionDenied } from '../ui';
import type { Destination } from '../destinations';

export interface GateProps {
  destination: Destination;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  permitted?: boolean;
  role?: string | null;
  children: ReactNode;
}

export function TemplateGate({ destination, loading, error, onRetry, permitted = true, role, children }: GateProps) {
  if (!permitted) return <PermissionDenied screen={destination.title} role={role} />;
  if (loading) return <LoadingRows rows={6} label={destination.title} />;
  if (error) {
    return (
      <FeedbackState state="error" title={`โหลด${destination.title}ไม่สำเร็จ`} onRetry={onRetry}>
        {error}
      </FeedbackState>
    );
  }
  return <>{children}</>;
}

/** A card with a heading — the one container every template composes from. */
export function Card({ title, sub, action, flush = false, danger = false, children }: {
  title?: string;
  sub?: ReactNode;
  action?: ReactNode;
  /** For a card whose body is a table: the table draws its own padding. */
  flush?: boolean;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={`jt-card ${danger ? 'jt-danger-zone' : ''}`.trim()}>
      {title && (
        <div className="jt-card-head">
          <div>
            {/* h2 under the shell's single h1 — the heading order is the shell's to keep correct. */}
            <h2 className="jt-card-title">{title}</h2>
            {sub && <div className="jt-card-sub">{sub}</div>}
          </div>
          {action}
        </div>
      )}
      <div className="jt-card-body" data-flush={flush || undefined}>{children}</div>
    </section>
  );
}
